'use client';

import { CollectionPaths } from '@order-up/common';
import { ILocation, ITenant, Location as LocationRecord, TenantDocId, asTenantDocId } from '@order-up/models';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { getFirebaseAuth } from '../firebase/config';
import { DocumentWithId, FirestoreRepository } from '../interfaces/FirestoreRepository';
import { locationService } from '../services/locations'; // scoped-path-ok: module path
import { createLogger } from '../utils/logger';

const logger = createLogger('SelectionContext');

// Types for our selection state
export interface SelectionState {
    tenantId: number | null;
    restaurantId: number | null;
    tenant: (ITenant & { docId?: TenantDocId }) | null;
    restaurant: (ILocation & { docId?: string }) | null;
}

export interface SelectionContextType {
    // Current selection state
    selection: SelectionState;

    // Actions
    setSelection: (tenant: ITenant & { docId?: TenantDocId }, locationRecord: ILocation & { docId?: string }) => void;
    clearSelection: () => void;

    // Utilities
    isSelected: boolean;
    hasValidSelection: boolean;

    /**
     * Firebase uid of the user who made this selection, or `null` when unknown
     * (nothing persisted, or a selection written by a build predating the
     * stamp).
     *
     * Consumers use this to tell "this browser's stored selection is MY own"
     * from "this could be the previous user's selection on a shared machine"
     * WITHOUT a server round-trip. When it matches the signed-in uid, tenant-
     * scoped UI can paint immediately and the server membership check becomes a
     * background revalidation instead of a gate on first paint.
     */
    ownerUid: string | null;
}

// Default state
const defaultState: SelectionState = {
    tenantId: null,
    restaurantId: null,
    tenant: null,
    restaurant: null,
};

// Create the context
const SelectionContext = createContext<SelectionContextType | undefined>(undefined);

// Storage keys for persistence
const STORAGE_KEYS = {
    TENANT_ID: 'selected_tenant_id',
    RESTAURANT_ID: 'selected_restaurant_id',
    TENANT_DATA: 'selected_tenant_data',
    RESTAURANT_DATA: 'selected_restaurant_data',
    // Whose selection this is. Note the `selected_tenant`/`selected_restaurant`
    // fragments that `clearSelectionStorage()` sweeps on — this key deliberately
    // contains `selected_tenant` so the namespace-agnostic sweep removes it too.
    OWNER_UID: 'selected_tenant_owner_uid',
} as const;

// Helper functions for localStorage
const storage = {
    get: (key: string): string | null => {
        if (typeof window === 'undefined') return null;
        try {
            return window.localStorage.getItem(key);
        } catch {
            return null;
        }
    },

    set: (key: string, value: string): void => {
        if (typeof window === 'undefined') return;
        try {
            window.localStorage.setItem(key, value);
        } catch {
            // Silently fail if localStorage is not available
        }
    },

    remove: (key: string): void => {
        if (typeof window === 'undefined') return;
        try {
            window.localStorage.removeItem(key);
        } catch {
            // Silently fail
        }
    },

    clear: (): void => {
        if (typeof window === 'undefined') return;
        try {
            Object.values(STORAGE_KEYS).forEach(key => window.localStorage.removeItem(key));
        } catch {
            // Silently fail
        }
    }
};

// Re-exported for back-compat: the namespace-agnostic localStorage sweep lives
// in a dependency-free util so sign-out paths can call it without importing this
// (React/Firebase-bearing) module.
export { clearSelectionStorage } from '../utils/selectionStorage';

// Provider component
interface SelectionProviderProps {
    children: React.ReactNode;
    /**
     * Optional app-specific namespace for storage isolation
     * Useful if different apps need different selection contexts
     */
    namespace?: string;
}

export function SelectionProvider({ children, namespace = 'default' }: SelectionProviderProps) {
    const [selection, setSelectionState] = useState<SelectionState>(defaultState);
    const [ownerUid, setOwnerUid] = useState<string | null>(null);
    const [isHydrated, setIsHydrated] = useState(false);
    const unsubscribeRef = useRef<(() => void) | null>(null);

    // Create namespaced storage keys
    const storageKeys = React.useMemo(() => ({
        TENANT_ID: `${namespace}_${STORAGE_KEYS.TENANT_ID}`,
        RESTAURANT_ID: `${namespace}_${STORAGE_KEYS.RESTAURANT_ID}`,
        TENANT_DATA: `${namespace}_${STORAGE_KEYS.TENANT_DATA}`,
        RESTAURANT_DATA: `${namespace}_${STORAGE_KEYS.RESTAURANT_DATA}`,
        OWNER_UID: `${namespace}_${STORAGE_KEYS.OWNER_UID}`,
    }), [namespace]);

    // Load persisted selection on mount
    useEffect(() => {
        const loadPersistedSelection = () => {
            try {
                const tenantIdStr = storage.get(storageKeys.TENANT_ID);
                const locationNumericIdStr = storage.get(storageKeys.RESTAURANT_ID);
                const tenantDataStr = storage.get(storageKeys.TENANT_DATA);
                const locationDataStr = storage.get(storageKeys.RESTAURANT_DATA);

                if (tenantIdStr && locationNumericIdStr && tenantDataStr && locationDataStr) {
                    const tenantData = JSON.parse(tenantDataStr);
                    const locationData = JSON.parse(locationDataStr);

                    setSelectionState({
                        tenantId: parseInt(tenantIdStr, 10),
                        restaurantId: parseInt(locationNumericIdStr, 10),
                        tenant: tenantData,
                        restaurant: locationData,
                    });

                    // Absent for selections written before the owner stamp
                    // existed — consumers then fall back to the server
                    // membership check, which is the pre-existing behaviour.
                    setOwnerUid(storage.get(storageKeys.OWNER_UID));

                    logger.debug('Restored persisted selection', {
                        tenantId: parseInt(tenantIdStr, 10),
                        restaurantId: parseInt(locationNumericIdStr, 10),
                    });
                }
            } catch (error) {
                logger.warn('Failed to load persisted selection', { error: error instanceof Error ? error.message : String(error) });
                // Clear corrupted data
                Object.values(storageKeys).forEach(key => storage.remove(key));
            }

            setIsHydrated(true);
        };

        loadPersistedSelection();
    }, [storageKeys]);

    // Real-time Firestore listener for the selected restaurant
    useEffect(() => {
        // Clean up previous listener
        if (unsubscribeRef.current) {
            unsubscribeRef.current();
            unsubscribeRef.current = null;
        }

        const { tenantId, restaurantId: locationNumericId } = selection;
        if (!tenantId || !locationNumericId || !isHydrated) return;

        let cancelled = false;

        const startListener = async () => {
            try {
                const tenantDocId = await locationService.resolveTenantDocId(tenantId);
                if (cancelled) return;
                if (!tenantDocId) {
                    // NOT a benign no-op. `selection.tenant.docId` stays
                    // undefined, so every tenant-scoped Firestore read that is
                    // gated on the doc id (integrations, books/connections)
                    // never runs, and the realtime listener below never starts.
                    // Silence here is what lets a broken selection render as
                    // "nothing is connected" instead of as an error.
                    logger.error('Could not resolve tenantDocId for selection', {
                        tenantId,
                        consequence:
                            'tenant-scoped reads (integrations, books) will not run for this session',
                    });
                    return;
                }

                // Self-heal selection.tenant.docId. Several setSelection callers
                // (and stale localStorage from older builds) leave the tenant
                // without a docId; the books/connections APIs require a non-empty
                // tenantDocId (e.g. GET /connections/lookup). Reuse the value we
                // just resolved. Fail-closed: we already returned above when the
                // resolve yielded null. Idempotent — guarded on the absent docId,
                // and patching tenant.docId does NOT re-trigger this effect (deps
                // key on tenantId, not the tenant object), so there is no loop.
                setSelectionState(prev => {
                    if (!prev.tenant || prev.tenant.docId) return prev;
                    // Resolver hands back a raw Firestore document id — brand it here, at
                    // the origin, so no downstream caller has to cast.
                    const healedTenant = { ...prev.tenant, docId: asTenantDocId(tenantDocId) };
                    try {
                        storage.set(storageKeys.TENANT_DATA, JSON.stringify(healedTenant));
                    } catch {
                        // Non-persistent is fine; the heal still applies in-memory.
                    }
                    logger.debug('Healed selection.tenant.docId', { tenantId, tenantDocId });
                    return { ...prev, tenant: healedTenant };
                });

                // Self-heal selection.restaurant.docId the same way. Stale
                // localStorage from older builds (and setSelection callers that
                // pass a restaurant without docId) leave it undefined until the
                // real-time snapshot below lands — and never, if the listener
                // fails to start. Consumers (hosting gate, storage contexts)
                // need it immediately, so resolve it at the source. The
                // session-cached resolver makes this a one-time query, and the
                // heal is idempotent (guarded on the absent docId; deps key on
                // ids, not the restaurant object, so no loop).
                if (!selection.restaurant?.docId) {
                    const locationDocId = await locationService.resolveRestaurantDocId(
                        tenantDocId,
                        locationNumericId,
                    );
                    if (locationDocId && !cancelled) {
                        setSelectionState(prev => {
                            if (!prev.restaurant || prev.restaurant.docId) return prev;
                            const healedLocation = { ...prev.restaurant, docId: locationDocId };
                            try {
                                storage.set(
                                    storageKeys.RESTAURANT_DATA,
                                    JSON.stringify(healedLocation),
                                );
                            } catch {
                                // Non-persistent is fine; the heal still applies in-memory.
                            }
                            logger.debug('Healed selection.restaurant.docId', {
                                restaurantId: locationNumericId,
                                restaurantDocId: locationDocId,
                            });
                            return { ...prev, restaurant: healedLocation };
                        });
                    }
                }
                if (cancelled) return;

                const collectionPath = CollectionPaths.restaurants(tenantDocId);
                const repo = new FirestoreRepository<LocationRecord>(collectionPath, LocationRecord);

                const unsubscribe = repo.subscribeToCollection(
                    [{ field: 'restaurantId', operator: '==', value: locationNumericId }],
                    [],
                    (locations: DocumentWithId<LocationRecord>[]) => {
                        if (cancelled || locations.length === 0) return;

                        const updatedLocation = locations[0];

                        setSelectionState(prev => {
                            // Only update if restaurant data actually changed
                            if (JSON.stringify(prev.restaurant) === JSON.stringify(updatedLocation)) {
                                return prev;
                            }

                            const newSelection: SelectionState = {
                                ...prev,
                                restaurant: updatedLocation,
                            };

                            // Persist updated restaurant data to localStorage
                            try {
                                storage.set(storageKeys.RESTAURANT_DATA, JSON.stringify(updatedLocation));
                            } catch {
                                // Silently fail
                            }

                            logger.debug('Restaurant data updated via real-time listener', {
                                restaurantId: updatedLocation.restaurantId,
                            });

                            return newSelection;
                        });
                    },
                    (error) => {
                        logger.warn('Restaurant listener error', { error: error.message });
                    }
                );

                if (cancelled) {
                    unsubscribe();
                } else {
                    unsubscribeRef.current = unsubscribe;
                }
            } catch (error) {
                logger.warn('Failed to start restaurant listener', {
                    error: error instanceof Error ? error.message : String(error),
                });
            }
        };

        startListener();

        return () => {
            cancelled = true;
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
                unsubscribeRef.current = null;
            }
        };
    }, [selection.tenantId, selection.restaurantId, isHydrated, storageKeys]);

    // Persist selection changes
    const persistSelection = (newSelection: SelectionState) => {
        if (!newSelection.tenant || !newSelection.restaurant) {
            // Clear storage if selection is incomplete
            Object.values(storageKeys).forEach(key => storage.remove(key));
            return;
        }

        try {
            storage.set(storageKeys.TENANT_ID, newSelection.tenantId!.toString());
            storage.set(storageKeys.RESTAURANT_ID, newSelection.restaurantId!.toString());
            storage.set(storageKeys.TENANT_DATA, JSON.stringify(newSelection.tenant));
            storage.set(storageKeys.RESTAURANT_DATA, JSON.stringify(newSelection.restaurant));

            // Stamp the owner so a later boot can tell "my own selection" from
            // "whoever used this browser last" without waiting on the server.
            // Best-effort: if Firebase isn't ready the stamp is simply absent
            // and consumers fall back to the server membership check.
            let uid: string | null = null;
            try {
                uid = getFirebaseAuth().currentUser?.uid ?? null;
            } catch {
                uid = null;
            }
            if (uid) {
                storage.set(storageKeys.OWNER_UID, uid);
                setOwnerUid(uid);
            } else {
                storage.remove(storageKeys.OWNER_UID);
                setOwnerUid(null);
            }

            logger.debug('Persisted selection', {
                tenantId: newSelection.tenantId,
                restaurantId: newSelection.restaurantId,
            });
        } catch (error) {
            logger.warn('Failed to persist selection', { error: error instanceof Error ? error.message : String(error) });
        }
    };

    // Actions
    const setSelection = (tenant: ITenant & { docId?: TenantDocId }, locationRecord: ILocation & { docId?: string }) => {
        const newSelection: SelectionState = {
            tenantId: tenant.tenantId,
            restaurantId: locationRecord.restaurantId,
            tenant,
            restaurant: locationRecord,
        };

        setSelectionState(newSelection);
        persistSelection(newSelection);

        logger.debug('Selection updated', {
            tenantId: tenant.tenantId,
            restaurantId: locationRecord.restaurantId,
            tenantName: tenant.name,
            restaurantName: locationRecord.name,
            tenantDocId: tenant.docId,
            restaurantDocId: locationRecord.docId,
        });
    };

    const clearSelection = () => {
        setSelectionState(defaultState);
        setOwnerUid(null);
        Object.values(storageKeys).forEach(key => storage.remove(key));
        logger.debug('Selection cleared');
    };

    // Computed values
    const isSelected = selection.tenantId !== null && selection.restaurantId !== null;
    const hasValidSelection = isSelected && selection.tenant !== null && selection.restaurant !== null;

    const contextValue: SelectionContextType = {
        selection,
        setSelection,
        clearSelection,
        isSelected,
        hasValidSelection,
        ownerUid,
    };

    // Always mount the context provider. Gating *children* (not the provider)
    // on hydration prevents SSR mismatch while guaranteeing the context is
    // never absent for any descendant — so `useSelection()` can never throw
    // due to a transient pre-hydration render.
    return (
        <SelectionContext.Provider value={contextValue}>
            {isHydrated ? children : null}
        </SelectionContext.Provider>
    );
}

/**
 * Non-throwing accessor: returns the selection context, or `undefined` when
 * no `SelectionProvider` is mounted. Use this in code paths shared between
 * management (which mounts the provider) and the public apps (ordering /
 * checkout / restaurant) which do NOT — they use `MenuSelectionProvider`.
 */
export function useOptionalSelection(): SelectionContextType | undefined {
    return useContext(SelectionContext);
}

// Custom hook to use the selection context. Throws when no provider is
// mounted — this is a hard invariant in the management app, where every
// authenticated route is wrapped in <SelectionProvider>. Public apps must
// NOT call this directly; use the convenience hooks below, which degrade
// gracefully, or read the restaurant from their own provider/props.
export function useSelection(): SelectionContextType {
    const context = useOptionalSelection();

    if (context === undefined) {
        throw new Error('useSelection must be used within a SelectionProvider');
    }

    return context;
}

// Convenience hooks for specific values. These intentionally degrade to a
// null selection when no SelectionProvider is present, so they are safe to
// call from public apps (e.g. via useTimeFormatter) without crashing.
export function useSelectedTenant() {
    const selection = useOptionalSelection()?.selection;
    return {
        tenantId: selection?.tenantId ?? null,
        tenant: selection?.tenant ?? null,
    };
}

export function useSelectedLocation() {
    const selection = useOptionalSelection()?.selection;
    return {
        restaurantId: selection?.restaurantId ?? null,
        restaurant: selection?.restaurant ?? null,
    };
}

// Type guard for checking if selection is complete
export function hasCompleteSelection(selection: SelectionState): selection is Required<SelectionState> {
    return (
        selection.tenantId !== null &&
        selection.restaurantId !== null &&
        selection.tenant !== null &&
        selection.restaurant !== null
    );
}

export default SelectionContext;
